import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { User } from '../api/types';

interface AuthCtx {
  user: User | null;
  setSession: (token: string, user: User) => void;
  logout: () => void;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [user, setUser] = useState<User | null>(() => {
    const raw = localStorage.getItem('automator_user');
    return raw ? JSON.parse(raw) : null;
  });

  const setSession = useCallback(
    (token: string, u: User) => {
      const prevRaw = localStorage.getItem('automator_user');
      const prevId = prevRaw ? JSON.parse(prevRaw).id : null;
      localStorage.setItem('automator_token', token);
      localStorage.setItem('automator_user', JSON.stringify(u));
      if (prevId !== u.id) qc.clear();
      setUser(u);
    },
    [qc],
  );

  const logout = useCallback(() => {
    localStorage.removeItem('automator_token');
    localStorage.removeItem('automator_user');
    qc.clear();
    setUser(null);
  }, [qc]);

  return <Ctx.Provider value={{ user, setSession, logout }}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth fora do AuthProvider');
  return ctx;
}
